// FilterChips.tsx - Extracted filter chips component

import { useTranslation } from "react-i18next";
import FilterChip from "./FilterChip";
import {
  getAgentName,
  getCampaignName,
  getAiComplianceFilterLabel,
  getFrequencyFilterLabel,
  getServiceName,
  getSupervisorName,
} from "../../../utils";
import { formatLocaleDate } from "../../../utils/commonUtils";
import type { TFilterChipsProps, TTableColumn } from "@/types/filter";
import { getEmployeeAttributeChipValue } from "./employeeAttributeUtils";

// Constants
const DATE_TYPE_LABELS: Record<string, string> = {
  call_date: "By Call Date",
  audit_date: "By Audit Date",
  employee_termination: "Termination Date",
  leave_begin: "Leave Start",
  leave_return: "Leave Expected Return",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getSearchQueryChipValue = (
  searchType: string | undefined,
  searchQuery: string | undefined,
  tableColumns: TTableColumn[],
): string => {
  const currentColumnMeta = tableColumns.find((c) => c.id === searchType)?.meta;
  if (!searchType || !searchQuery) return "";

  if (currentColumnMeta?.filterSearchType === "checkbox") {
    return searchQuery.toLowerCase() === "true" ? "Yes" : "No";
  }

  if (currentColumnMeta?.filterSearchType === "dropdown") {
    return (
      currentColumnMeta?.filterSearchOptions?.find(
        (c) => c.value === searchQuery,
      )?.label ?? ""
    );
  }
  if (currentColumnMeta?.filterSearchType === "date") {
    return formatLocaleDate(new Date(searchQuery));
  }

  if (
    (searchType === "ai_score" || searchType === "qa_score") &&
    ["n/a", "na"].includes(searchQuery.toLowerCase())
  ) {
    return "N/A";
  }

  return searchQuery;
};

// Using types from the centralized types file

const FilterChips = ({
  appliedFilters,
  capabilities,
  campaignData,
  agentListData,
  serviceData,
  entityOptions,
  templateData,
  locationOptions,
  qaSupervisorData,
  rankBasedAuditorData,
  employeeAttributeDefinitions,
  languageData,
  tableColumns = [],
  component = "",
  statusData,
  integrationPartner,
  aiEngineData,
  onClearFilter,
  id,
}: TFilterChipsProps) => {
  const { t } = useTranslation([
    "common",
    "evaluationListingPage",
    "roleManagementPage",
    "overAllSentimentAnalysisPage",
  ]);
  const employeeAttributes = employeeAttributeDefinitions
    ? Object.values(employeeAttributeDefinitions).flat()
    : [];
  const employeeAttributeChip = getEmployeeAttributeChipValue(
    employeeAttributes,
    appliedFilters.employeeAttrKey ?? "",
    appliedFilters.employeeAttrValue ?? "",
  );
  const campaignIds = appliedFilters.campaign?.id?.split(",") ?? [];
  const campaignLabel =
    appliedFilters.campaign?.name ||
    campaignIds
      .map(
        (campaignId) =>
          getCampaignName(campaignData?.results ?? [], campaignId) ||
          campaignData?.selectedFallBacksOptions?.find(
            (option) => option.value === campaignId,
          )?.label ||
          campaignId,
      )
      .join(", ");

  const timePeriodOptions = [
    {
      value: "this-week",
      label: t(
        "overAllSentimentAnalysisPage:FILTERS.TIME_PERIODS.THIS_WEEK",
        "This Week",
      ),
    },
    {
      value: "last-week",
      label: t("overAllSentimentAnalysisPage:FILTERS.TIME_PERIODS.LAST_WEEK"),
    },
    {
      value: "last-month",
      label: t("overAllSentimentAnalysisPage:FILTERS.TIME_PERIODS.LAST_MONTH"),
    },
    {
      value: "week-2",
      label: t(
        "overAllSentimentAnalysisPage:FILTERS.TIME_PERIODS.TWO_WEEKS_AGO",
      ),
    },
    {
      value: "week-3",
      label: t(
        "overAllSentimentAnalysisPage:FILTERS.TIME_PERIODS.THREE_WEEKS_AGO",
      ),
    },
    {
      value: "week-4",
      label: t(
        "overAllSentimentAnalysisPage:FILTERS.TIME_PERIODS.FOUR_WEEKS_AGO",
      ),
    },
    {
      value: "month-2",
      label: t(
        "overAllSentimentAnalysisPage:FILTERS.TIME_PERIODS.TWO_MONTHS_AGO",
      ),
    },
    {
      value: "month-3",
      label: t(
        "overAllSentimentAnalysisPage:FILTERS.TIME_PERIODS.THREE_MONTHS_AGO",
      ),
    },
    {
      value: "select-customize-date",
      label: "Select Date",
    },
  ];

  return (
    <div className="flex flex-wrap justify-start gap-2">
      {/* Date Type Chip */}
      {capabilities.hasDateType && appliedFilters.dateType && (
        <FilterChip
          variant="blue"
          label={t("FILTER.DATE_TYPE")}
          value={DATE_TYPE_LABELS[appliedFilters.dateType ?? ""] || ""}
          showCloseIcon={false}
          onClear={() => onClearFilter("date_type", "")}
          data-testid="date-type-filter-chip"
          id={`${id}-date-type-chip`}
        />
      )}

      {/* Date Range Chip */}
      {capabilities.hasStartEndDate && appliedFilters.startDate && (
        <FilterChip
          variant="green"
          label={
            component === "push-api-clients" ||
            component === "ai-engine-configurations"
              ? t("FILTER.DATE_RANGE")
              : appliedFilters.dateType === "audit_date"
                ? t("FILTER.AUDIT_DATE_RANGE")
                : t("FILTER.CALL_DATE_RANGE")
          }
          value={`${formatLocaleDate(new Date(appliedFilters.startDate))} to ${appliedFilters.endDate ? formatLocaleDate(new Date(appliedFilters.endDate)) : formatLocaleDate(new Date())}`}
          showCloseIcon={
            component === "push-api-clients" ||
            component === "ai-engine-configurations"
          }
          onClear={() => onClearFilter("date_range", "")}
          ariaLabel={
            component === "push-api-clients" ||
            component === "ai-engine-configurations"
              ? "Clear date range filter"
              : undefined
          }
          data-testid="date-range-filter-chip"
          id={`${id}-date-range-chip`}
        />
      )}

      {/* Year Chip */}
      {capabilities.hasYearMonth && appliedFilters.year && (
        <FilterChip
          variant="amber"
          label={t("common:FILTER.YEAR")}
          value={appliedFilters.year}
          showCloseIcon={false}
          onClear={() => onClearFilter("year", "")}
          data-testid="year-filter-chip"
          id={`${id}-year-chip`}
        />
      )}

      {/* Month Chip */}
      {capabilities.hasYearMonth && appliedFilters.month && (
        <FilterChip
          variant="amber"
          label={t("common:FILTER.MONTH")}
          value={
            appliedFilters.month
              ? MONTH_NAMES[parseInt(appliedFilters.month, 10) - 1]
              : ""
          }
          showCloseIcon={false}
          data-testid="month-filter-chip"
          id={`${id}-month-chip`}
        />
      )}

      {/* Entity Chip */}
      {capabilities.hasEntity && appliedFilters.entity && (
        <FilterChip
          variant="blue"
          label="Entity"
          value={
            entityOptions?.results?.find(
              (e) => e.internal_id === appliedFilters.entity,
            )?.name ?? ""
          }
          onClear={() => onClearFilter("entity", "")}
          ariaLabel="Clear entity filter"
          data-testid="entity-filter-chip"
          id={`${id}-entity-chip`}
        />
      )}

      {/* Campaign Chip */}
      {capabilities.hasCampaign && appliedFilters.campaign?.id && (
        <FilterChip
          variant="green"
          label={t("FILTER.CAMPAIGN")}
          value={campaignLabel}
          onClear={() => onClearFilter("campaign", "")}
          ariaLabel="Clear campaign filter"
          data-testid="campaign-filter-chip"
          id={`${id}-campaign-chip`}
        />
      )}

      {/* STT Target Language Chip */}
      {capabilities.hasTargetLanguage &&
        (appliedFilters.targetLanguageIds?.length ?? 0) > 0 && (
          <FilterChip
            variant="indigo"
            label={t("FILTER.TARGET_LANGUAGE")}
            value={(appliedFilters.targetLanguageIds ?? [])
              .map(
                (languageId) =>
                  languageData?.results?.find(
                    (language) => language.internal_id === languageId,
                  )?.name ?? languageId,
              )
              .join(", ")}
            onClear={() => onClearFilter("targetLanguage", [])}
            ariaLabel="Clear target language filter"
            data-testid="target-language-filter-chip"
            id={`${id}-target-language-chip`}
          />
        )}

      {/* Agent Chip */}
      {capabilities.hasAgent && appliedFilters.agent?.id && (
        <FilterChip
          variant="indigo"
          label={t("FILTER.AGENT")}
          value={
            appliedFilters.agent.name ||
            getAgentName(
              agentListData?.results ?? [],
              appliedFilters.agent.id,
            ) ||
            agentListData?.selectedFallBacksOptions?.find(
              (o) => o.value === appliedFilters.agent?.id,
            )?.label ||
            appliedFilters.agent.id
          }
          onClear={() => onClearFilter("agent", "")}
          ariaLabel="Clear agent filter"
          data-testid="agent-filter-chip"
          id={`${id}-agent-chip`}
        />
      )}

      {/* Service Chip */}
      {capabilities.hasService && appliedFilters.service?.id && (
        <FilterChip
          variant="blue"
          label={t("overAllSentimentAnalysisPage:FILTERS.SERVICE")}
          value={
            appliedFilters.service.name ||
            getServiceName(
              serviceData?.results ?? [],
              appliedFilters.service.id,
            ) ||
            serviceData?.selectedFallBacksOptions?.find(
              (o) => o.value === appliedFilters.service?.id,
            )?.label ||
            appliedFilters.service.id
          }
          onClear={() => onClearFilter("service", "")}
          ariaLabel="Clear service filter"
          data-testid="service-filter-chip"
          id={`${id}-service-chip`}
        />
      )}

      {/* Status Chip */}
      {appliedFilters?.status !== undefined && capabilities.hasStatus && (
        <FilterChip
          variant="blue"
          label={t("FILTER.STATUS")}
          value={
            statusData?.results?.find(
              (e) => e.value === appliedFilters?.status?.toString()?.trim(),
            )?.status ?? ""
          }
          onClear={() => onClearFilter("status", "")}
          ariaLabel="Clear status filter"
          data-testid="status-filter-chip"
          id={`${id}-status-chip`}
          showCloseIcon={!capabilities.withoutClearFilterChipButton}
        />
      )}

      {/* Location Chip */}
      {capabilities.hasLocation && appliedFilters.location && (
        <FilterChip
          variant="amber"
          label={t("FILTER.LOCATION")}
          value={
            appliedFilters?.location && Array.isArray(locationOptions?.results)
              ? (locationOptions?.results?.find(
                  (item) => item?.internal_id === appliedFilters.location,
                )?.name ??
                locationOptions?.selectedFallBacksOptions?.find(
                  (o) => o.value === appliedFilters.location,
                )?.label)
              : ""
          }
          onClear={() => onClearFilter("location", "")}
          ariaLabel="Clear location filter"
          data-testid="location-filter-chip"
          id={`${id}-location-chip`}
        />
      )}

      {/* Search Query Chip */}
      {capabilities?.hasSearchDropdown &&
        appliedFilters.searchType &&
        appliedFilters.searchQuery && (
          <FilterChip
            variant="indigo"
            label={(() => {
              const header = tableColumns?.find(
                (col) => col.id === appliedFilters.searchType,
              )?.header;
              return typeof header === "string"
                ? header
                : (appliedFilters.searchType?.replace(/_/g, " ") ?? "");
            })()}
            value={getSearchQueryChipValue(
              appliedFilters.searchType,
              appliedFilters.searchQuery,
              tableColumns,
            )}
            onClear={() => {
              onClearFilter("searchQuery", "");
              onClearFilter("searchType", "");
            }}
            ariaLabel="Clear search filter"
            data-testid="search-query-filter-chip"
            id={`${id}-search-query-chip`}
          />
        )}

      {/* Permission Count Range Chip */}
      {((capabilities?.hasPermissionCountRange &&
        appliedFilters.permissionCountMin) ??
        appliedFilters.permissionCountMax) && (
        <FilterChip
          variant="amber"
          label={t(
            "roleManagementPage:ROLES.SEARCH_FILTERS.PERMISSION_COUNT_RANGE",
          )}
          value={`${appliedFilters?.permissionCountMin ?? ""} - ${appliedFilters?.permissionCountMax ?? ""}`}
          onClear={() => {
            onClearFilter("permissionCount");
          }}
          ariaLabel="Clear permission count range filter"
          data-testid="permission-count-range-filter-chip"
          id={`${id}-permission-count-range-chip`}
        />
      )}

      {/* User Count Range Chip */}
      {((capabilities?.hasPermissionCountRange &&
        appliedFilters.userCountMin) ??
        appliedFilters.userCountMax) && (
        <FilterChip
          variant="green"
          label={t("roleManagementPage:ROLES.SEARCH_FILTERS.USER_COUNT_RANGE")}
          value={`${appliedFilters?.userCountMin ?? ""} - ${appliedFilters?.userCountMax ?? ""}`}
          onClear={() => {
            onClearFilter("userCount");
          }}
          ariaLabel="Clear user count range filter"
          data-testid="user-count-range-filter-chip"
          id={`${id}-user-count-range-chip`}
        />
      )}

      {/* Period Value Chip */}
      {capabilities?.hasWeeklyPeriod &&
        appliedFilters.periodValue &&
        appliedFilters.periodValue !== "select-customize-date" && (
          <FilterChip
            variant="amber"
            label={t("overAllSentimentAnalysisPage:FILTERS.PERIOD")}
            value={
              timePeriodOptions?.find(
                (item) => item?.value === appliedFilters?.periodValue,
              )?.label ?? ""
            }
            onClear={() => onClearFilter("period")}
            data-testid="period-filter-chip"
            id={`${id}-period-chip`}
          />
        )}

      {/* Template Chip */}
      {capabilities?.hasTemplate && appliedFilters.template?.id && (
        <FilterChip
          variant="amber"
          label={t("FILTER.TEMPLATE")}
          value={
            appliedFilters?.template.id && Array.isArray(templateData?.results)
              ? (templateData?.results?.find(
                  (item) => item?.internal_id === appliedFilters?.template?.id,
                )?.name ??
                templateData?.selectedFallBacksOptions?.find(
                  (o) => o.value === appliedFilters?.template?.id,
                )?.label)
              : ""
          }
          onClear={() => onClearFilter("template", "")}
          ariaLabel="Clear template filter"
          data-testid="template-filter-chip"
          id={`${id}-template-chip`}
        />
      )}

      {/* Supervisor Chip */}
      {capabilities?.hasSupervisor && appliedFilters.supervisor?.id && (
        <FilterChip
          variant="indigo"
          label={t("FILTER.QASUPERVISOR")}
          value={
            appliedFilters.supervisor.name ||
            getSupervisorName(
              qaSupervisorData?.results ?? [],
              appliedFilters.supervisor.id,
            ) ||
            qaSupervisorData?.selectedFallBacksOptions?.find(
              (o) => o.value === appliedFilters.supervisor?.id,
            )?.label ||
            appliedFilters.supervisor.id
          }
          onClear={() => onClearFilter("supervisor", "")}
          ariaLabel={"Clear supervisor filter"}
          data-testid="supervisor-filter-chip"
          id={`${id}-supervisor-chip`}
        />
      )}

      {/* Auditor Chip */}
      {capabilities?.hasRankBasedAuditor &&
        appliedFilters.rankBasedAuditorId && (
          <FilterChip
            variant="indigo"
            label={t("FILTER.AUDITOR")}
            value={
              appliedFilters.rankBasedAuditorId &&
              Array.isArray(rankBasedAuditorData)
                ? getSupervisorName(
                    rankBasedAuditorData,
                    appliedFilters.rankBasedAuditorId,
                  )
                : ""
            }
            onClear={() => onClearFilter("rankBasedAuditor", "")}
            ariaLabel={"Clear auditor filter"}
            data-testid="rankBasedAuditor-filter-chip"
            id={`${id}-rankBasedAuditor-chip`}
          />
        )}

      {/* Score Chip */}
      {capabilities?.hasScore && appliedFilters?.score && (
        <FilterChip
          variant="blue"
          label={t("FILTER.SCORE")}
          value={appliedFilters?.score ? `< ${appliedFilters?.score}%` : ""}
          onClear={() => onClearFilter("score", "")}
          ariaLabel="Clear score filter"
          data-testid="score-filter-chip"
          id={`${id}-score-chip`}
        />
      )}

      {/* AI Engine Chip */}
      {capabilities.hasAiEngine && appliedFilters.aiEngine && (
        <FilterChip
          variant="blue"
          label={t("FILTER.AI_ENGINE")}
          value={
            aiEngineData?.results?.find(
              (item) =>
                (item.name || item.slug || item.internal_id) ===
                appliedFilters.aiEngine,
            )?.name ||
            aiEngineData?.selectedFallBacksOptions?.find(
              (item) => item.value === appliedFilters.aiEngine,
            )?.label ||
            appliedFilters.aiEngine
          }
          onClear={() => onClearFilter("aiEngine", "")}
          ariaLabel="Clear AI engine filter"
          data-testid="ai-engine-filter-chip"
          id={`${id}-ai-engine-chip`}
        />
      )}

      {/* Frequency Chip */}
      {capabilities.hasFrequency && appliedFilters.frequency && (
        <FilterChip
          variant="amber"
          label={t("FILTER.FREQUENCY")}
          value={getFrequencyFilterLabel(appliedFilters.frequency)}
          onClear={() => onClearFilter("frequency", "")}
          ariaLabel="Clear frequency filter"
          data-testid="frequency-filter-chip"
          id={`${id}-frequency-chip`}
        />
      )}

      {/* AI Compliance Chip */}
      {capabilities.hasAiCompliance && appliedFilters.aiCompliance && (
        <FilterChip
          variant="green"
          label={t("FILTER.AI_COMPLIANCE")}
          value={getAiComplianceFilterLabel(appliedFilters.aiCompliance)}
          onClear={() => onClearFilter("aiCompliance", "")}
          ariaLabel="Clear AI compliance filter"
          data-testid="ai-compliance-filter-chip"
          id={`${id}-ai-compliance-chip`}
        />
      )}

      {/* Integration type Chip */}
      {capabilities.hasIngestionType && appliedFilters.type && (
        <FilterChip
          variant="amber"
          label="Type"
          value={
            integrationPartner?.results?.find(
              (e) => e.internal_id === appliedFilters.type,
            )?.name ?? ""
          }
          onClear={() => onClearFilter("type", "")}
          ariaLabel="Clear integrationType filter"
          data-testid="integration-type-filter-chip"
          id={`${id}-integration-type-chip`}
        />
      )}

      {/* CSAT Number Input Chip */}
      {capabilities?.hasCSAT &&
        appliedFilters.csat !== undefined &&
        appliedFilters.csat !== "" && (
          <FilterChip
            variant="amber"
            label={t("FILTER.CSAT")}
            value={appliedFilters.csat}
            onClear={() => onClearFilter("csat", "")}
            ariaLabel="Clear csat input filter"
            data-testid="csat-input-filter-chip"
            id={`${id}-csat-input-chip`}
          />
        )}

      {/* FCR Number Input Chip */}
      {capabilities?.hasFCR &&
        appliedFilters.fcr !== undefined &&
        appliedFilters.fcr !== "" && (
          <FilterChip
            variant="indigo"
            label={t("FILTER.FCR")}
            value={appliedFilters.fcr}
            onClear={() => onClearFilter("fcr", "")}
            ariaLabel="Clear fcr input filter"
            data-testid="fcr-input-filter-chip"
            id={`${id}-fcr-input-chip`}
          />
        )}

      {/* Profanity Number Input Chip */}
      {capabilities?.hasProfanity &&
        appliedFilters.profanity !== undefined &&
        appliedFilters.profanity !== "" && (
          <FilterChip
            variant="green"
            label={t("FILTER.PROFANITY")}
            value={appliedFilters.profanity}
            onClear={() => onClearFilter("profanity", "")}
            ariaLabel="Clear profanity input filter"
            data-testid="profanity-input-filter-chip"
            id={`${id}-profanity-input-chip`}
          />
        )}

      {/* AI Score Range Chip */}
      {capabilities?.hasAIScoreRange &&
        (appliedFilters.aiScoreMin !== "" ||
          appliedFilters.aiScoreMax !== "") && (
          <FilterChip
            variant="blue"
            label="AI Score"
            value={`${appliedFilters.aiScoreMin || "0"} - ${appliedFilters.aiScoreMax || "100"}`}
            onClear={() => onClearFilter("aiScore", "")}
            ariaLabel="Clear AI score filter"
            data-testid="ai-score-range-filter-chip"
            id={`${id}-ai-score-range-chip`}
          />
        )}

      {/* QA Score Range Chip */}
      {capabilities?.hasQAScoreRange &&
        (appliedFilters.qaScoreMin !== "" ||
          appliedFilters.qaScoreMax !== "") && (
          <FilterChip
            variant="blue"
            label="QA Score"
            value={`${appliedFilters.qaScoreMin || "0"} - ${appliedFilters.qaScoreMax || "100"}`}
            onClear={() => onClearFilter("qaScore", "")}
            ariaLabel="Clear QA score filter"
            data-testid="qa-score-range-filter-chip"
            id={`${id}-qa-score-range-chip`}
          />
        )}

      {/* Achievement Chip */}
      {(capabilities?.hasAchievement ||
        component === "agent-performance-list") &&
        appliedFilters.achievement && (
          <FilterChip
            variant="amber"
            label="Achievement"
            value={appliedFilters.achievement
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")}
            onClear={() => onClearFilter("achievement", "")}
            ariaLabel="Clear achievement filter"
            data-testid="achievement-filter-chip"
            id={`${id}-achievement-chip`}
          />
        )}
      {capabilities?.hasEmployeeAttrDropdown &&
        appliedFilters.employeeAttrKey &&
        appliedFilters.employeeAttrValue && (
          <FilterChip
            key="employee-attribute"
            variant="indigo"
            label={employeeAttributeChip.label}
            value={employeeAttributeChip.value}
            onClear={() => onClearFilter("employeeAttrValue", "")}
            ariaLabel="Clear employee attribute filter"
            data-testid="employee-attribute-filter-chip"
            id={`${id}-employee-attribute-chip`}
          />
        )}
    </div>
  );
};

export default FilterChips;
